// Controls
import { Button, Tooltip } from "@fluentui/react-components";
import { PanelLeftContract, PanelLeftContractFilled, PanelLeftExpand, PanelLeftExpandFilled } from "@ms/planner-generic-controls/lib/local/icons/index";
import { SwitchViewDropdown } from "@ms/planner-presentational-controls/lib/local/switchViewDropdown/SwitchViewDropdown";
import plannerSvg from "@ms/uno-resources/lib/local/images/logos/planner2.svg";
import { LeftNavNewPlanSection } from "./LeftNavNewPlanSection";
import { LeftNavPinnedSection } from "./LeftNavPinnedSection";
import { LeftNavStaticSection } from "./LeftNavStaticSection";
// Constants
import { KeyCode } from "@ms/uno-constants/lib/local/KeyCode";
import { LeftNavCollapseThreshold } from "@ms/uno-constants/lib/local/UIConstants";
// Styles
import { useLeftNavStyles } from "./LeftNav.styles";
// Utilities
import { mergeClasses } from "@fluentui/react-components";
import { KeyboardUtilities } from "@ms/uno-utilities/lib/local/KeyboardUtilities";
import * as React from "react";
import { Strings } from "../../../utilities/CultureUtilities";
/**
 * LeftNav is the root component for the left navigation
 */ export const LeftNav = ({ className, staticOptions, pinnedOptions, newPlanInfo, customKeyCodes, switchViewDropdownOptions, shouldRenderPinnedError, showAppInfo, onRetryButtonClicked, calloutDetails, isLeftNavCollapsed, setIsLeftNavCollapsed, isAppInBackground })=>{
    const styles = useLeftNavStyles();
    const [isToggleHovered, setIsToggleHovered] = React.useState(false);
    // Track if user has manually interacted with toggle
    const hasUserToggledRef = React.useRef(false);
    // Ref to hold the handleResize function - updated on every render with fresh logic
    const handleResizeRef = React.useRef(()=>{});
    handleResizeRef.current = ()=>{
        // Skip if app is in background
        if (isAppInBackground) {
            return;
        }
        const shouldCollapse = window.innerWidth <= LeftNavCollapseThreshold;
        // Reset user toggle flag when window becomes large enough
        // This allows auto-collapse to work again on subsequent resize to small screen
        if (!shouldCollapse) {
            hasUserToggledRef.current = false;
            return;
        }
        // Only auto-collapse if user hasn't manually toggled and we're not already collapsed
        if (shouldCollapse && !isLeftNavCollapsed && !hasUserToggledRef.current) {
            setIsLeftNavCollapsed(true);
        }
    };
    // Check initial screen size and when relevant props change
    React.useEffect(()=>{
        handleResizeRef.current();
    }, [
        isLeftNavCollapsed,
        isAppInBackground
    ]);
    // Subscribe to resize event once on mount, unsubscribe on unmount
    React.useEffect(()=>{
        const onResize = ()=>handleResizeRef.current();
        window.addEventListener("resize", onResize);
        return ()=>window.removeEventListener("resize", onResize);
    }, []);
    /**
     * Toggle the left nav expanded/collapsed state
     */ const onToggleLeftNav = ()=>{
        hasUserToggledRef.current = true;
        setIsLeftNavCollapsed(!isLeftNavCollapsed);
    };
    /**
     * Handle keyboard events for the toggle button
     */ const handleToggleKeyDown = (event)=>{
        const keyCode = KeyboardUtilities.getKey(event);
        if (keyCode === KeyCode.ENTER) {
            event.preventDefault();
            onToggleLeftNav();
        }
    };
    /**
     * Get the style for the toggle button based on the current state
     */ const getToggleButtonStyle = ()=>{
        if (!isLeftNavCollapsed) {
            if (showAppInfo) {
                return styles.toggleButtonWithAppInfo;
            } else {
                return styles.toggleButtonWithoutAppInfo;
            }
        }
        return undefined;
    };
    /**
     * Keycode mapper for child components
     */ const keyCodeMapper = (keyCode)=>{
        const keyCodes = customKeyCodes ?? {
            executeActionKeyCode: KeyCode.ENTER
        };
        switch(keyCode){
            case KeyCode.ENTER:
                return keyCodes.executeActionKeyCode;
            default:
                return keyCode;
        }
    };
    /**
     * Render the app header with logo and toggle button
     */ const renderAppHeader = ()=>{
        const isRTL = document.dir === "rtl";
        const tooltipContent = isLeftNavCollapsed ? Strings.LeftNav_OpenPaneTooltip : Strings.LeftNav_ClosePaneTooltip;
        const expandIcons = {
            normalIcon: /*#__PURE__*/ React.createElement(PanelLeftExpand, null),
            focusedIcon: /*#__PURE__*/ React.createElement(PanelLeftExpandFilled, null)
        };
        const contractIcons = {
            normalIcon: /*#__PURE__*/ React.createElement(PanelLeftContract, null),
            focusedIcon: /*#__PURE__*/ React.createElement(PanelLeftContractFilled, null)
        };
        const icon = isLeftNavCollapsed ? expandIcons : contractIcons;
        const toggleIcon = isToggleHovered && icon.focusedIcon || icon.normalIcon;
        return /*#__PURE__*/ React.createElement("div", {
            className: mergeClasses(styles.appHeader, isLeftNavCollapsed && styles.appHeaderCollapsed)
        }, showAppInfo && !isLeftNavCollapsed && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement("img", {
            src: plannerSvg,
            alt: Strings.LeftNav_PlannerLogoText,
            className: styles.appLogo
        }), /*#__PURE__*/ React.createElement("h1", {
            className: styles.appHeaderTitle
        }, Strings.LeftNav_PlannerText)), /*#__PURE__*/ React.createElement(Tooltip, {
            content: tooltipContent,
            relationship: "description"
        }, /*#__PURE__*/ React.createElement("div", {
            className: getToggleButtonStyle()
        }, /*#__PURE__*/ React.createElement(Button, {
            appearance: "transparent",
            onClick: onToggleLeftNav,
            onKeyDown: handleToggleKeyDown,
            onMouseEnter: ()=>setIsToggleHovered(true),
            onMouseLeave: ()=>setIsToggleHovered(false),
            onFocus: ()=>setIsToggleHovered(true),
            onBlur: ()=>setIsToggleHovered(false),
            "aria-label": isLeftNavCollapsed ? Strings.LeftNav_ExpandButtonAriaLabel : Strings.LeftNav_CollapseButtonAriaLabel,
            "aria-expanded": !isLeftNavCollapsed,
            "aria-controls": "left-nav-content",
            className: mergeClasses(styles.toggleButton, isRTL && styles.toggleButtonRTL)
        }, toggleIcon))));
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: mergeClasses(styles.root, isLeftNavCollapsed && styles.collapsed, className)
    }, renderAppHeader(), !isLeftNavCollapsed && /*#__PURE__*/ React.createElement("div", {
        id: "left-nav-content",
        className: styles.leftNavContent,
        role: "navigation",
        "aria-label": Strings.LeftNav_MainNavigationAriaLabel
    }, /*#__PURE__*/ React.createElement("div", {
        className: styles.newPlanSection
    }, /*#__PURE__*/ React.createElement(LeftNavNewPlanSection, {
        newPlanInfo: newPlanInfo,
        isLeftNavCollapsed: isLeftNavCollapsed,
        keyCodeMapper: keyCodeMapper,
        calloutDetails: calloutDetails
    })), /*#__PURE__*/ React.createElement("div", {
        className: styles.scrollableSection,
        "data-testid": "left-nav-scrollable-section"
    }, /*#__PURE__*/ React.createElement(SwitchViewDropdown, {
        currentView: switchViewDropdownOptions.currentView,
        isPublisherViewVisible: switchViewDropdownOptions.isPublisherViewVisible,
        className: switchViewDropdownOptions.className,
        onChange: switchViewDropdownOptions.onChange
    }), /*#__PURE__*/ React.createElement(LeftNavStaticSection, {
        staticOptions: staticOptions.staticOptions,
        isLeftNavCollapsed: isLeftNavCollapsed,
        keyCodeMapper: keyCodeMapper
    }), /*#__PURE__*/ React.createElement(LeftNavPinnedSection, {
        pinnedOptions: pinnedOptions,
        isLeftNavCollapsed: isLeftNavCollapsed,
        keyCodeMapper: keyCodeMapper,
        shouldRenderPinnedError: shouldRenderPinnedError,
        onRetryButtonClicked: onRetryButtonClicked
    }))));
};
